import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import {ArrowRight, Check, Star, Dumbbell, Users, Clock, Zap, ChevronLeft, ChevronRight} from 'lucide-react';
import { Link } from 'react-router-dom';

const testimonials = [
  {
    name: "Marion Henrion",
    role: "Il y a 2 mois",
    text: "Salle de sport agréable, accueil sympathique. Les cours collectifs sont top ! Personnel disponible et à l'écoute.",
    rating: 5
  },
  {
    name: "Nicolas Bernard",
    role: "Il y a 3 mois",
    text: "Super salle, très bien équipée avec du matériel récent. L'ambiance est conviviale et les coachs sont vraiment professionnels.",
    rating: 5
  },
  {
    name: "Sarah Martinez",
    role: "Il y a 1 mois",
    text: "Je recommande à 100% ! Salle propre, moderne et spacieuse. Les cours de Zumba sont géniaux, parfait pour se défouler.",
    rating: 5
  },
  {
    name: "Thomas Dubois",
    role: "Il y a 4 mois",
    text: "Excellente salle de musculation ! Très bon rapport qualité/prix, équipements variés et parking gratuit. Top !",
    rating: 5
  },
  {
    name: "Julie Renard",
    role: "Il y a 2 semaines",
    text: "L'accueil est chaleureux, l'équipe est super sympa. Les cours collectifs sont dynamiques et motivants !",
    rating: 5
  },
  {
    name: "Alexandre Petit",
    role: "Il y a 5 mois",
    text: "Salle au top avec une super ambiance ! Les machines sont en excellent état et il y a toujours de la place.",
    rating: 4
  },
  {
    name: "Camille Laurent",
    role: "Il y a 3 semaines",
    text: "Je suis ravie ! Les cours de Pilates sont parfaits et les coachs donnent d'excellents conseils personnalisés.",
    rating: 5
  },
  {
    name: "Marc Fontaine",
    role: "Il y a 6 mois",
    text: "Très bonne salle, propre et bien organisée. Horaires larges qui s'adaptent parfaitement à mon emploi du temps.",
    rating: 5
  },
  {
    name: "Sophie Moreau",
    role: "Il y a 1 mois",
    text: "Ambiance familiale et équipe super accueillante ! Les installations sont modernes et toujours impeccables.",
    rating: 5
  }
];

const TestimonialCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [itemsPerPage, setItemsPerPage] = useState(3);

  // Detect screen size and adjust items per page
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setItemsPerPage(1);
      } else {
        setItemsPerPage(3);
      }
    };

    handleResize(); // Initial check
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const totalPages = Math.ceil(testimonials.length / itemsPerPage);

  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % totalPages);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, totalPages]);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % totalPages);
    setIsAutoPlaying(false);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + totalPages) % totalPages);
    setIsAutoPlaying(false);
  };

  const visibleReviews = testimonials.slice(currentIndex * itemsPerPage, currentIndex * itemsPerPage + itemsPerPage);

  return (
    <div className="relative">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
        {visibleReviews.map((review, idx) => (
          <div key={idx} className="bg-black p-8 rounded-2xl border border-white/5 relative transform transition-all duration-500 hover:border-nolimit-red/50">
            <div className="absolute -top-4 left-8 text-6xl text-nolimit-red/20 font-serif">"</div>
            <div className="flex mb-4">
              {[...Array(review.rating)].map((_, i) => (
                <Star key={i} className="h-5 w-5 text-yellow-500 fill-yellow-500" />
              ))}
            </div>
            <p className="text-gray-300 mb-6 relative z-10 min-h-[100px]">{review.text}</p>
            <div className="flex items-center">
              <div className="w-10 h-10 bg-gradient-to-br from-nolimit-red to-red-700 rounded-full mr-3 flex items-center justify-center font-bold text-white shadow-lg">
                {review.name.charAt(0)}
              </div>
              <div>
                <h4 className="font-bold text-white">{review.name}</h4>
                <p className="text-xs text-gray-500">{review.role}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Navigation Controls */}
      <div className="flex items-center justify-center gap-4">
        <button
          onClick={prevSlide}
          className="w-12 h-12 bg-zinc-800 hover:bg-nolimit-red rounded-full flex items-center justify-center transition-all group"
          aria-label="Avis précédents"
        >
          <ChevronLeft className="h-6 w-6 text-white" />
        </button>
        
        <div className="flex gap-2">
          {[...Array(totalPages)].map((_, idx) => (
            <button
              key={idx}
              onClick={() => {
                setCurrentIndex(idx);
                setIsAutoPlaying(false);
              }}
              className={`h-2 rounded-full transition-all ${
                idx === currentIndex 
                  ? 'w-8 bg-nolimit-red' 
                  : 'w-2 bg-zinc-700 hover:bg-zinc-600'
              }`}
              aria-label={`Aller à la page ${idx + 1}`}
            />
          ))}
        </div>

        <button
          onClick={nextSlide}
          className="w-12 h-12 bg-zinc-800 hover:bg-nolimit-red rounded-full flex items-center justify-center transition-all group"
          aria-label="Avis suivants"
        >
          <ChevronRight className="h-6 w-6 text-white" />
        </button>
      </div>
    </div>
  );
};

const Home = () => {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative h-[90vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://static.lumi.new/d9/d92f9990b5e004b992cce1eab83c7304.webp" 
            alt="Salle de sport moderne" 
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30" />
          <div className="absolute inset-0 bg-gradient-radial from-transparent via-black/40 to-black/60" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block mb-4 px-4 py-1.5 rounded-full bg-nolimit-red/20 border border-nolimit-red/30 backdrop-blur-sm">
            <span className="text-nolimit-red font-bold tracking-wider text-sm uppercase">Le sport sans limites à Toul</span>
          </div>
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-black text-white mb-6 tracking-tighter italic">
            DÉPASSEZ VOS <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-nolimit-red to-red-600">LIMITES</span>
          </h1>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto leading-relaxed">
            Rejoignez la communauté No Limit Fitness. Équipements haut de gamme, coachs passionnés et ambiance survoltée pour atteindre vos objectifs.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/contact">
              <Button size="lg" className="w-full sm:w-auto group">
                Séance d'essai offerte <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/planning">
              <Button variant="outline" size="lg" className="w-full sm:w-auto">
                Voir le planning
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: <Dumbbell className="h-8 w-8 text-black" />,
                title: "Équipement Premium",
                desc: "Parc machines Matrix dernière génération pour des entraînements optimaux."
              },
              {
                icon: <Users className="h-8 w-8 text-black" />,
                title: "Cours Collectifs",
                desc: "Plus de 30 cours par semaine : Yoga, Pump, HIIT, Zumba et plus encore."
              },
              {
                icon: <Clock className="h-8 w-8 text-black" />,
                title: "Ouvert 7j/7",
                desc: "Accès libre de 06h00 à 23h00 pour s'adapter à votre rythme de vie."
              }
            ].map((feature, idx) => (
              <div key={idx} className="bg-zinc-900 p-8 rounded-2xl border border-white/5 hover:border-nolimit-red/50 transition-colors group">
                <div className="w-16 h-16 bg-nolimit-red rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  {feature.icon}
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">{feature.title}</h3>
                <p className="text-gray-400 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Image Split Section */}
      <section className="py-0 bg-zinc-900 overflow-hidden">
        <div className="flex flex-col md:flex-row">
          <div className="md:w-1/2 h-[500px] relative">
            <img 
              src="https://static.lumi.new/ca/ca8769b22d68251ca4bbe51a9828a1af.png" 
              alt="Coaching personnalisé" 
              className="w-full h-full object-cover"
            />
          </div>
          <div className="md:w-1/2 p-12 md:p-20 flex flex-col justify-center">
            <h2 className="text-4xl font-black italic text-white mb-6">
              COACHING <span className="text-nolimit-red">PERSONNALISÉ</span>
            </h2>
            <p className="text-gray-300 text-lg mb-8 leading-relaxed">
              Ne vous entraînez plus seul. Nos coachs certifiés créent pour vous des programmes sur-mesure adaptés à votre morphologie et vos objectifs. Progression garantie et motivation constante.
            </p>
            <ul className="space-y-4 mb-10">
              {[
                "Bilan corporel complet offert",
                "Programmes évolutifs mensuels",
                "Suivi nutritionnel adapté",
                "Correction de posture en temps réel"
              ].map((item, i) => (
                <li key={i} className="flex items-center text-gray-200">
                  <Check className="h-5 w-5 text-nolimit-red mr-3" />
                  {item}
                </li>
              ))}
            </ul>
            <Link to="/contact">
              <Button variant="secondary">Rencontrer un coach</Button>
            </Link>
          </div>
        </div>
      </section>

       {/* Class Section */}
      <section className="py-24 bg-black relative">
         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-center gap-16">
                <div className="md:w-1/2 order-2 md:order-1">
                    <h2 className="text-4xl font-black italic text-white mb-6">
                    L'ÉNERGIE DU <span className="text-nolimit-red">GROUPE</span>
                    </h2>
                    <p className="text-gray-300 text-lg mb-8 leading-relaxed">
                    Laissez-vous porter par l'énergie collective. Nos cours sont conçus pour brûler un maximum de calories dans une ambiance fun et motivante.
                    </p>
                    <div className="grid grid-cols-2 gap-4 mb-8">
                        <div className="p-4 bg-zinc-900 rounded-lg border-l-4 border-nolimit-red">
                            <h4 className="font-bold text-white">Cardio Intense</h4>
                            <p className="text-sm text-gray-400">SPINNING / ATTACK / FIGHT</p>
                        </div>
                        <div className="p-4 bg-zinc-900 rounded-lg border-l-4 border-purple-500">
                            <h4 className="font-bold text-white">Zen & Souplesse</h4>
                            <p className="text-sm text-gray-400">PILATES / FAC</p>
                        </div>
                        <div className="p-4 bg-zinc-900 rounded-lg border-l-4 border-blue-500">
                            <h4 className="font-bold text-white">Renforcement</h4>
                            <p className="text-sm text-gray-400">TRX / CIRCUIT / FULL BODY</p>
                        </div>
                        <div className="p-4 bg-zinc-900 rounded-lg border-l-4 border-orange-500">
                            <h4 className="font-bold text-white">STEP & Jumping</h4>
                            <p className="text-sm text-gray-400">STEP / JUMPING / WORKOUT</p>
                        </div>
                    </div>
                    <Link to="/planning">
                        <Button>Découvrir le planning</Button>
                    </Link>
                </div>
                <div className="md:w-1/2 order-1 md:order-2 h-[500px] rounded-2xl overflow-hidden relative">
                    <img 
                        src="https://static.lumi.new/15/15dacb8e2c6165e29337adc10609bd41.webp"
                        alt="Cours collectif - Workout avec Abel"
                        className="w-full h-full object-cover"
                    />
                </div>
            </div>
         </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-zinc-900 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black italic text-white mb-4">
              ILS ONT <span className="text-nolimit-red">TRANSFORMÉ</span> LEUR VIE
            </h2>
            <p className="text-gray-400 mb-2">Découvrez les avis de nos adhérents</p>
            <div className="flex items-center justify-center gap-2 text-yellow-500">
              <Star className="h-5 w-5 fill-yellow-500" />
              <span className="font-bold text-white">4.5/5</span>
              <span className="text-gray-400 text-sm">(50+ avis Google)</span>
            </div>
          </div>
          
          <TestimonialCarousel />
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-zinc-900">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black italic text-white mb-4">
              QUESTIONS <span className="text-nolimit-red">FRÉQUENTES</span>
            </h2>
            <p className="text-gray-400">Tout ce que vous devez savoir sur No Limit Fitness</p>
          </div>

          <div className="space-y-4">
            {[
              {
                question: "Quels sont les horaires d'ouverture ?",
                answer: "Nous sommes ouverts 7j/7 de 6h00 à 23h00, y compris les week-ends et jours fériés. Profitez d'un accès libre pour vous entraîner à votre rythme, quand vous le souhaitez !"
              },
              {
                question: "Y a-t-il des frais d'adhésion ?",
                answer: "Non, aucun frais d'adhésion ni frais de dossier ! Vous ne payez que votre abonnement mensuel ou votre formule choisie. Nous croyons en la transparence et la simplicité."
              },
              {
                question: "Est-ce que le parking est gratuit ?",
                answer: "Oui ! Un parking gratuit et spacieux est à votre disposition juste devant la salle. Plus d'excuses pour ne pas venir s'entraîner ! 🚗"
              },
              {
                question: "Puis-je essayer la salle avant de m'inscrire ?",
                answer: "Absolument ! Nous offrons une séance d'essai gratuite et sans engagement pour que vous puissiez découvrir nos équipements, nos cours collectifs et l'ambiance conviviale du club."
              },
              {
                question: "Quels types de cours collectifs proposez-vous ?",
                answer: "Nous proposons plus de 30 cours par semaine : Yoga, Pilates, Zumba, RPM, Body Pump, Body Combat, Stretching, Cross Training, HIIT et bien plus encore ! Il y en a pour tous les goûts et tous les niveaux."
              },
              {
                question: "Faut-il réserver pour les cours collectifs ?",
                answer: "La plupart de nos cours sont en accès libre, mais certains cours populaires nécessitent une réservation via notre application mobile ou à l'accueil. Consultez notre planning en ligne pour plus d'informations."
              },
              {
                question: "Proposez-vous du coaching personnalisé ?",
                answer: "Oui ! Nos coachs certifiés créent des programmes sur-mesure adaptés à vos objectifs. Bilan corporel offert, suivi nutritionnel, correction de posture en temps réel... On vous accompagne jusqu'à la réussite !"
              },
              {
                question: "La salle est-elle adaptée aux débutants ?",
                answer: "Bien sûr ! Nos coachs sont là pour vous accueillir et vous guider. Équipements intuitifs, cours débutants spécifiques et ambiance bienveillante : vous êtes au bon endroit pour commencer votre transformation."
              },
              {
                question: "Où êtes-vous situés ?",
                answer: "Nous sommes au 155 Rue Robert Schumann à Écrouves (54200), à seulement quelques minutes de Toul. Accès facile, parking gratuit et salle moderne vous attendent !"
              }
            ].map((faq, idx) => (
              <details key={idx} className="bg-black border border-white/10 rounded-lg group hover:border-nolimit-red/50 transition-all">
                <summary className="cursor-pointer p-6 font-bold text-white flex items-center justify-between group-hover:text-nolimit-red transition-colors">
                  <span className="text-lg">{faq.question}</span>
                  <span className="text-nolimit-red text-2xl group-open:rotate-45 transition-transform">+</span>
                </summary>
                <div className="px-6 pb-6 text-gray-400 leading-relaxed border-t border-white/5 pt-4">
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-400 mb-6">Vous avez d'autres questions ?</p>
            <Link to="/contact">
              <Button variant="outline" size="lg">
                Contactez-nous
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-nolimit-red relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pattern-grid-lg bg-black" />
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <h2 className="text-5xl font-black text-black mb-6 italic uppercase tracking-tighter">
            Prêt à commencer ?
          </h2>
          <p className="text-xl text-white/90 mb-10 font-medium">
            Profitez d'une séance d'essai gratuite et sans engagement. Venez tester nos équipements et l'ambiance du club.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
                <button className="bg-white text-nolimit-red hover:bg-gray-100 px-8 py-4 rounded-full font-bold text-lg transition-all transform hover:scale-105 shadow-xl">
                RÉSERVER MA SÉANCE GRATUITE
                </button>
            </Link>
            <Link to="/tarifs">
                <button className="bg-transparent text-white hover:bg-white/10 px-8 py-4 rounded-full font-bold text-lg transition-all border-2 border-white">
                VOIR LES TARIFS
                </button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Home;
